import { useRef } from "react";

import { FieldHint, FilterLabel } from "../AdminPage.styles";
import {
  EditorContainer,
  EditorCount,
  EditorFooter,
  EditorTextArea,
  VariableChip,
  VariableList,
  VariableListLabel,
} from "./LongTextEditor.styles";

interface ILongTextEditorProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Fills a split editor/preview pane. */
  tall?: boolean;
  /** Soft-wrap instead of scrolling sideways — right for prose, not markup. */
  wrap?: boolean;
  hint?: string;
  /**
   * Placeholder names a template may reference. Clicking one inserts
   * `{{ name }}` at the cursor.
   */
  variables?: string[];
}

/**
 * The one long-text editor for the /admin pages — the AI prompt body
 * (EN-5161) and the email template HTML (EN-5162). Both tickets asked for a
 * design decision on editing long text and asked for the two to be aligned,
 * so it lives in `pages/Admin/shared`.
 *
 * A monospace, vertically resizable textarea rather than a rich editor:
 *
 * - Both values must round-trip **byte for byte**. A prompt is fed to a model
 *   and an email template is HTML; a rich editor normalises whitespace and
 *   rewrites markup, which would silently change what is stored. Nothing here
 *   trims, reflows or re-indents the value.
 * - Retool used a plain `<TextArea>` for the prompt and a bespoke Retool
 *   custom component (`PMpCustomComponents_HtmlEditorViewer`) for the email
 *   HTML. The custom component can't be ported — it's a Retool plugin — so
 *   the portal needs its own treatment either way, and one that behaves the
 *   same in both places is worth more than matching a widget we're deleting.
 *
 * Tab inserts a tab character instead of leaving the field, which is what an
 * operator indenting a prompt or nested HTML expects.
 */
export function LongTextEditor({
  id,
  label,
  value,
  onChange,
  disabled = false,
  tall = false,
  wrap = false,
  hint,
  variables,
}: ILongTextEditorProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  /** Inserts text at the cursor, keeping undo history and cursor position. */
  const insertAtCursor = (snippet: string) => {
    const textArea = textAreaRef.current;
    if (!textArea) return;

    const start = textArea.selectionStart ?? value.length;
    const end = textArea.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${snippet}${value.slice(end)}`);

    // Restore the caret after React re-renders with the new value.
    requestAnimationFrame(() => {
      const caret = start + snippet.length;
      textArea.focus();
      textArea.setSelectionRange(caret, caret);
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Tab" || event.shiftKey) return;
    event.preventDefault();
    insertAtCursor("\t");
  };

  return (
    <EditorContainer>
      <FilterLabel htmlFor={id}>{label}</FilterLabel>

      {variables && variables.length > 0 && (
        <VariableList>
          <VariableListLabel>Insert a placeholder:</VariableListLabel>
          {variables.map((variable) => (
            <VariableChip
              key={variable}
              type="button"
              disabled={disabled}
              title={`Insert {{ ${variable} }}`}
              onClick={() => insertAtCursor(`{{ ${variable} }}`)}
            >
              {variable}
            </VariableChip>
          ))}
        </VariableList>
      )}

      <EditorTextArea
        id={id}
        ref={textAreaRef}
        tall={tall}
        wrap={wrap}
        disabled={disabled}
        value={value}
        spellCheck={false}
        onKeyDown={handleKeyDown}
        onChange={(e) => onChange(e.target.value)}
      />

      <EditorFooter>
        {hint ? <FieldHint>{hint}</FieldHint> : null}
        <EditorCount>{value.length.toLocaleString()} characters</EditorCount>
      </EditorFooter>
    </EditorContainer>
  );
}
